import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import { PAGE_STATE } from "../../../constants";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import { format } from "date-fns";
import Button from "../../common/Button";
import TextArea from "../../common/TextArea";
import { Form, Formik } from "formik";
import Slider from "react-slick";
import { toast } from "react-toastify";
import "../../../styles/reasonForm.scss";

const RejectModal = ({ userId, onReturn, onSuccess }) => {
  const { rejectUser, patchUser } = useUsers();

  const handleSubmit = ({ reason }, _actions) => {
    Promise.allSettled([
      rejectUser(userId, reason),
      patchUser(userId, { status: 3 }),
    ]).then(([rejectResult, patchResult]) => {
      if (
        rejectResult.status !== "fulfilled" ||
        patchResult.status !== "fulfilled"
      ) {
        toast.error("Unable to reject");
        return;
      }

      onSuccess();
    });
  };

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      onClose={onReturn}
      content={
        <div className="reason-form">
          <h1 className="reason-form__title">What is the reason?</h1>
          <Formik
            initialValues={{ reason: "" }}
            validate={({ reason }) => {
              if (!reason) {
                return {
                  reason: "Cannot leave blank",
                };
              }
              return {};
            }}
            onSubmit={handleSubmit}
          >
            {() => {
              return (
                <Form>
                  <TextArea name="reason" />
                  <div className="reason-form-cta">
                    <Button
                      label="Return"
                      variant="secondary"
                      onClick={onReturn}
                    />
                    <Button label="Submit" type="submit" />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      }
    />
  );
};

const Customer = ({ userId, onClose }) => {
  const { getUser, getUserRejections, patchUser } = useUsers();
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [user, setUser] = useState(undefined);
  const { plans } = useContext(AppContext);
  const [toggleRejectReason, setToggleRejectReason] = useState(false);
  const [rejections, setRejections] = useState(undefined);

  useEffect(() => {
    init();
    return () => {};
  }, []);

  const init = async () => {
    const [userResult, rejectionsResult] = await Promise.allSettled([
      getUser(userId),
      getUserRejections(userId),
    ]);

    if (userResult.status === "fulfilled") {
      setUser(userResult.value);
    }

    if (rejectionsResult.status === "fulfilled") {
      setRejections(rejectionsResult.value);
    }

    setPageState(PAGE_STATE.IDLE);
  };

  if (toggleRejectReason) {
    return (
      <RejectModal
        userId={userId}
        onReturn={() => setToggleRejectReason(false)}
        onSuccess={onClose}
      />
    );
  }

  if (pageState === PAGE_STATE.LOADING || !user) {
    return <Overlay content={<>Loading</>} onClose={onClose} />;
  }

  const handleApproveClick = async () => {
    await patchUser(user._id, { status: 2 });
    setUser((curr) => ({ ...curr, status: 2 }));
    onClose();
  };

  const getStatus = () => {
    if (user.status === 2) {
      return (
        <label
          className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--2`}`}
        >
          Approved
        </label>
      );
    }

    if (rejections.length > 0) {
      return (
        <label
          className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--3`}`}
        >
          Rejected
        </label>
      );
    }

    return (
      <label
        className={`admin-verification-user-info-status__value ${`admin-verification-user-info-status__value--1`}`}
      >
        Pending
      </label>
    );
  };

  const displayDetail = (title, value) => {
    return (
      <>
        <label className="admin-verification-user-info-details__title">
          {title}
        </label>
        <label className="admin-verification-user-info-details__value">
          {value}
        </label>
      </>
    );
  };

  const { firstname, surname, plan, dateOfBirth, student, contact, status } =
    user;

  const planName = plans.find((p) => p._id === plan)?.name;

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dotsClass: "admin-verification-carousel-dots",
    customPaging: function (i) {
      return <div className="admin-verification-carousel-dot" />;
    },
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
  };

  return (
    <Overlay
      content={
        <div className="admin-verification-user-info">
          <h2 className="admin-verification-user-info__title">{planName}</h2>
          <div className="admin-verification-user-info-status">
            <label className="admin-verification-user-info-status__title">
              Verification status
            </label>
            {getStatus()}
          </div>
          <h3 className="admin-verification-user-info__heading">Information</h3>
          <div className="admin-verification-user-info-details">
            {displayDetail("Firstname", firstname)}
            {displayDetail("Surname", surname)}
            {dateOfBirth &&
              displayDetail(
                "Date of birth",
                format(new Date(dateOfBirth), "dd MMMM yyyy")
              )}
            {planName === "Student" && (
              <>
                {displayDetail("Student ID", student?.code)}
                {displayDetail("Identification No", student?.code)}
                {displayDetail("University or School", student?.university)}
                {displayDetail("Major", student?.major)}
                {displayDetail("Degree", student?.degree)}
                {displayDetail(
                  "Studying period",
                  `${student?.startingYear || ""} - ${
                    student?.endingYear || ""
                  }`
                )}
              </>
            )}
          </div>
          <h3 className="admin-verification-user-info__heading">Address</h3>
          <div className="admin-verification-user-info-details">
            {displayDetail("Street address", contact?.address?.streetAddress)}
            {displayDetail("Apt.", contact?.address?.apt)}
            {displayDetail("Sub District", contact?.address?.subDistrict)}
            {displayDetail("District", contact?.address?.district)}
            {displayDetail("Province", contact?.address?.province)}
            {displayDetail("Postal code", contact?.address?.postalCode)}
          </div>
          {rejections?.length > 0 && (
            <>
              <h3 className="admin-verification-user-info__heading">
                Past Rejections
              </h3>
              <div className="admin-verification-user-info-rejections">
                <Slider {...sliderSettings}>
                  {rejections.map((r) => {
                    return (
                      <div>
                        <div className="admin-verification-rejection">
                          <div className="admin-verification-rejection__reason">
                            {r.reason}
                          </div>
                          <div className="admin-verification-rejection__date">
                            On{" "}
                            {format(new Date(r.rejectedOn), "dd/MM/yyyy HH:mm")}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </>
          )}

          <div className="admin-verification-user-info-ctas">
            {user.status === 3 || user.status === 1 ? (
              <Button
                label="Reject"
                onClick={() => setToggleRejectReason(true)}
                variant={"secondary"}
              />
            ) : (
              <div />
            )}
            {user.status !== 2 && (
              <Button label="Approve" onClick={handleApproveClick} />
            )}
          </div>
        </div>
      }
      style={{
        alignItems: "flex-start",
      }}
      onClose={onClose}
    />
  );
};

Customer.propTypes = {
  userId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

Customer.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
};

export default Customer;
